import { ArrowUpRight, CirclePlus, Sparkles, Wrench } from "lucide-react";

import { Card } from "@/modern/components/ui/card";
import { cn } from "@/modern/utils";

import type { ChangelogCategory, ChangelogEntry } from "./changelogTypes";

const CATEGORY_LABEL: Record<ChangelogCategory, string> = {
  feature: "Feature",
  fix: "Fix",
  improvement: "Improvement",
};

const CATEGORY_ICON = {
  feature: CirclePlus,
  fix: Wrench,
  improvement: ArrowUpRight,
} as const;

/**
 * Full-field identity paint — category is the object identity.
 * Never use muted-foreground on these fills.
 */
const CATEGORY_FIELD: Record<
  ChangelogCategory,
  { field: string; title: string; body: string; meta: string }
> = {
  feature: {
    field: "bg-cobalt text-cobalt-foreground",
    title: "text-cobalt-foreground",
    body: "text-cobalt-foreground/75",
    meta: "text-cobalt-foreground/70",
  },
  improvement: {
    field: "bg-ochre text-ochre-foreground",
    title: "text-ochre-foreground",
    body: "text-ochre-foreground/75",
    meta: "text-ochre-foreground/70",
  },
  fix: {
    field: "bg-slate text-slate-foreground",
    title: "text-slate-foreground",
    body: "text-slate-foreground/75",
    meta: "text-slate-foreground/70",
  },
};

const TILE_RADIUS_CLS = "rounded-[1.5rem]";
const EYEBROW_CLS =
  "text-[10px] font-medium uppercase tracking-[0.18em]";

interface ChangelogEntryRowProps {
  entry: ChangelogEntry;
  /** Relative time label (e.g. "1 year ago"). */
  relativeDate?: string;
  isNew?: boolean;
  /** Featured mosaic tile for the summary orientation view. */
  variant?: "default" | "summary";
  className?: string;
}

/**
 * Identity mosaic cell — compact destination/list craft.
 * Eyebrow + icon TR · title + body · relative date at bottom.
 */
export function ChangelogEntryRow({
  entry,
  relativeDate,
  isNew = false,
  variant = "default",
  className,
}: ChangelogEntryRowProps) {
  const isSummary = variant === "summary";
  const Icon = CATEGORY_ICON[entry.category];
  const tone = CATEGORY_FIELD[entry.category];

  return (
    <Card
      variant="mosaic"
      className={cn(
        TILE_RADIUS_CLS,
        tone.field,
        "relative flex flex-col justify-between overflow-hidden shadow-none",
        isSummary ? "min-h-[9.5rem] p-5" : "min-h-[7.5rem] p-5",
        className
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className={cn("m-0 min-w-0 truncate", EYEBROW_CLS, tone.meta)}>
          {CATEGORY_LABEL[entry.category]}
        </p>
        <div className={cn("flex shrink-0 items-center gap-1.5", tone.meta)}>
          {isNew ? (
            <span className={cn("inline-flex items-center gap-1", EYEBROW_CLS)}>
              <Sparkles className="size-3 opacity-80" aria-hidden />
              New
            </span>
          ) : null}
          <Icon
            className={cn(
              "shrink-0 opacity-80",
              isSummary ? "size-5" : "size-4"
            )}
            aria-hidden
          />
        </div>
      </div>

      <div className={cn("mt-auto", isSummary ? "pt-3" : "pt-3")}>
        <h3
          className={cn(
            "font-medium leading-snug tracking-tight",
            tone.title,
            isSummary ? "text-lg" : "text-base"
          )}
        >
          {entry.title}
        </h3>
        <p
          className={cn(
            "mt-1.5 text-sm leading-relaxed",
            tone.body,
            isSummary ? "line-clamp-3" : undefined
          )}
        >
          {entry.description}
        </p>
        {relativeDate ? (
          <p className={cn("m-0 mt-3 text-xs font-medium", tone.meta)}>
            {relativeDate}
          </p>
        ) : null}
      </div>
    </Card>
  );
}
